import { pageDocument } from '../doc-blocks'
import { tableGrid } from '@w/content-blocks/src/table-html'
import { duplicateCaretBlock, duplicates } from './duplicate-block'
import { questionIds } from './unsaved-copy'
import { questionAction } from './question-actions'
import { atomicGesture, removeSelectedNodes, removeTopLevel } from './atomic-selection'
import { createTable, editTable, inlineLeaves, insertionFormat, insertRichBlock, linkRange, safeLinkHref, selectedTableRows } from './rich-content'
import { canonicalDraft } from './canonical-blocks'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { Change } from '@w/crdt'
import { liveScope, scopeBlocks } from './scope'
import { assertLevels, inInlineSlot } from './levels'
import { blockGap, findBlock, type Row } from './tree-read'
import { blockClass, blockRange, gridSlot, isSelectableBlock, selectedBlocks } from './block-selection'
import { prepareQuestionMetadata } from '../question-metadata'
import {
  beginOperation, editText, deleteRange as deleteInlineRange, insertBlock, insertBlockAfter, joinBlocks,
  insertChars, replaceBlock, replaceChildren, setBlockProps, spliceBlocks, takeInlineTail,
} from './ops'
import { formatBlock, formatBlockRange } from './format-block'
import { indentItem, listItemAction } from './lists'
import { indentCode, moveCodeLines } from './code'
import { paste, pasteBlocks } from './paste'
import { setCodeLanguage } from './code-language'
import { resizeColumns } from './columns'
import { dropBlocks, dropText } from './drop'
import { pastedComments } from './clipboard'
import { navigateHistory, recordHistory, historyGroup, type HistoryTiming } from './history'
import { mayEndShortcut, shortcuts } from './shortcuts'
import { applySlashItem } from './slash-menu'
import { formatAt, move } from './selection'
import { joinBoundary, split } from './structure'
import { UnsupportedIntentError, type EditorIntent } from './intents'
import { emptyParagraphFormat, rememberEmptyParagraphFormat, restoredSelection,
  type CorePoint, type CoreSelection, type CoreState } from './state'
import {
  children, clean, gapOf, gapPoint, isRow, lastOf, leaf, locate, plain, pointAt, remove, rows, sliceInline,
} from './tree'

/** Code-block delimiter pairs (BlockSuite code autopair). */
const CODE_PAIRS: Partial<Record<string, string>> = { '(': ')', '[': ']', '{': '}', '"': '"' }

export interface CoreResult {
  state: CoreState
  change: Change | null
}
// clearFormatting carries every inline format bit and always clears.
const formats = { formatBold: 1, formatItalic: 2, formatUnderline: 8, formatStrikeThrough: 4, formatCode: 16, clearFormatting: 31 }

export function applyIntent(
  previous: CoreState, intent: EditorIntent, timing: HistoryTiming = {},
): CoreResult {
  if (intent.type === 'historyUndo' || intent.type === 'historyRedo') {
    return navigateHistory(previous, intent.type === 'historyRedo')
  }
  if (intent.type === 'moveCaret' && intent.unit === 'visualLine') {
    throw new UnsupportedIntentError(`Core does not support ${intent.type}`)
  }
  // INV-FIELD: commands naming blocks outside the field (handle drops, sibling insertion, block settings) address the
  // document, so they leave the field scope first.
  const named = intent.type === 'dropBlocks' ? [...intent.blocks, intent.target]
    : intent.type === 'listItemAction' ? [intent.item]
    : intent.type === 'insertBlockSibling' || intent.type === 'setCodeLanguage' ? [intent.block]
      : intent.type === 'resizeColumns' ? [intent.left, intent.right] : []
  if (previous.scope && named.some(id => !findBlock(previous.blocks, id))) {
    return applyIntent({ ...previous, scope: undefined, blocks: scopeBlocks(previous.doc), selection: null, pendingFormat: undefined }, intent, timing)
  }
  const state = { ...previous, blocks: draftBlocks(previous, intent),
    selection: structuredClone(previous.selection) }
  state.pendingFormat ??= emptyParagraphFormat(state)
  const group = historyGroup(previous, intent, timing)
  const sections: { before: CoreState; after: CoreState; transform: boolean }[] = []
  const metadata = (): CoreState => ({ ...state, selection: structuredClone(state.selection),
    emptyParagraphFormats: structuredClone(state.emptyParagraphFormats) })
  const change = state.doc.edit(tx => {
    beginOperation(state, tx)
    let transformed = true
    const commands: Iterable<EditorIntent> = intent.type === 'insertText' && intent.text
      ? textRuns(intent.text, () => transformed) : [intent]
    for (const command of commands) {
      const before = metadata()
      const start = tx.counter
      applyCommand(state, command)
      if (tx.counter > start) sections.push({ before, after: metadata(), transform: false })
      if (command.type === 'insertText' && command.text) {
        tx.historyGroup()
        const typed = metadata()
        const transformStart = tx.counter
        transformed = shortcuts(state)
        if (transformed) {
          state.blocks = canonicalDraft(state.blocks)
          rememberEmptyParagraphFormat(state)
        }
        if (tx.counter > transformStart) sections.push({ before: typed, after: metadata(), transform: true })
        tx.historyGroup(group)
      }
    }
    prepareQuestionMetadata(tx)
    // INV-LEVEL: no mutation leaves block-level content in an inline-only slot (levels.ts); checked before it commits.
    assertLevels(previous.blocks, state.blocks, state.scope?.field)
  }, { group })
  const tokens = change.historyTokens ?? (change.historyToken ? [change.historyToken] : [])
  for (const [index, section] of sections.entries()) {
    const recorded = { ...section.after, history: state.history }
    recordHistory({ ...section.before, history: state.history }, recorded,
      { ...change, historyToken: tokens[index] }, intent, timing, section.transform ? undefined : group, section.transform)
    state.history = recorded.history
  }
  if (sections.length === 0) recordHistory(previous, state, change, intent, timing, group)
  const scope = liveScope(state.doc, state.scope)
  const scopeLost = !!state.scope && !scope
  state.scope = scope
  state.blocks = scopeBlocks(state.doc, scope)
  if (scopeLost) state.selection = null
  else if (intent.type === 'questionAction') state.selection = restoredSelection(state.selection, previous.blocks, state.blocks)
  const changed = change.changed
  return { state, change: changed ? change : null }
}

/** Intents whose writes stay within the rows their selection touches and the rows beside them. */
const CARET_INTENTS = new Set<EditorIntent['type']>(['insertText', 'insertParagraph', 'insertLineBreak',
  'deleteContentBackward', 'deleteContentForward', 'deleteWordBackward', 'deleteWordForward', 'deleteSoftLineBackward',
  'deleteSoftLineForward', 'moveCaret', 'formatBold', 'formatItalic', 'formatUnderline', 'formatStrikeThrough',
  'clearFormatting', 'insertFromPaste'])

/**
 * The block tree a command mutates in place. The committed view is deep-frozen (crdt view), so the command works on a
 * copy: a caret or text-range intent copies the top-level list and deep-copies only the top-level blocks from the one
 * before its first row's predecessor to the one after its last row's successor, the reach of a join, a split and a
 * markdown shortcut; every other block stays the frozen view object, which no write can change (a write to it throws)
 * and which the normalizers skip (canonicalDraft, clean, assertLevels). Lexical clones only the nodes an update writes
 * ($getWritable). Any other intent, a scope, a node, cell or table selection copies the whole tree.
 */
function draftBlocks(previous: CoreState, intent: EditorIntent): Blocks {
  const { blocks, selection } = previous
  if (!CARET_INTENTS.has(intent.type) || previous.scope || !selection || selection.nodes?.length || selection.cell ||
    selection.table || selection.field || !Object.isFrozen(blocks)) return structuredClone(blocks)
  const entries = rows(blocks)
  if (entries.length === 0) return structuredClone(blocks)
  const ends = [locate(entries, selection.anchor).row, locate(entries, selection.focus).row]
  const top = (row: number) => {
    const entry = entries[Math.max(0, Math.min(entries.length - 1, row))]
    return blocks.indexOf(entry.parents[0] ?? entry.block)
  }
  const first = top(Math.min(...ends) - 1)
  const last = top(Math.max(...ends) + 1)
  if (first < 0 || last < 0) return structuredClone(blocks)
  const from = Math.max(0, first - 1)
  const to = Math.min(blocks.length - 1, last + 1)
  const draft = blocks.slice()
  for (let index = from; index <= to; index += 1) draft[index] = structuredClone(blocks[index])
  return draft
}

/**
 * Inserted text as the runs typing it grapheme by grapheme would commit. A grapheme after a run that ended in a markdown
 * transform goes alone (the transform moved the caret and may have changed the block); every other run extends up to and
 * including the next grapheme that can end a shortcut (`mayEndShortcut`), so the shortcut check runs wherever it can fire
 * and nowhere else. One insertion per run instead of per grapheme: a 5,000-character paste is one edit, not 5,000.
 */
function* textRuns(text: string, alone: () => boolean): Generator<EditorIntent> {
  const graphemes = Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text), ({ segment }) => segment)
  for (let at = 0; at < graphemes.length;) {
    let end = at + 1
    if (!alone()) while (end < graphemes.length && !mayEndShortcut(graphemes[end - 1])) end += 1
    yield { type: 'insertText', text: graphemes.slice(at, end).join('') }
    at = end
  }
}

/**
 * Select all covers the page body. Lexical $selectAll normalizes element boundaries toward their first/last text
 * descendant (a wrapper at an edge keeps its shell when the selection is deleted); an atom at an edge is covered whole,
 * from the block gap beside it, so replacing or deleting the selection removes it. The header above the body is edited
 * in its own fields (Lexical: the title is outside the editor).
 */
function documentSelection(blocks: Blocks): CoreSelection {
  const edge = (end: boolean, siblings: Blocks, parent?: Block): CorePoint => {
    const block = end ? siblings.at(-1) : siblings[0]
    if (block && isRow(block)) {
      const row = rows([block])[0]
      return pointAt(row, end ? plain(row.block).length : 0)
    }
    if (block && blockClass(block) !== 'atom' && children(block).length > 0) return edge(end, children(block), block)
    return gapPoint({ siblings, parent, index: end ? siblings.length : 0 })
  }
  if (blocks.length === 1 && blocks[0].type === 'table') {
    return { anchor: gapPoint({ siblings: blocks, index: 0 }), focus: gapPoint({ siblings: blocks, index: 1 }) }
  }
  const body = Math.max(0, blocks.findIndex(block => blockClass(block) !== 'header'))
  return { anchor: edge(false, blocks.slice(body)), focus: edge(true, blocks) }
}

/**
 * The selection select all takes from `selection` (null: the first row's start). Inside a table cell: the cell's
 * text, then the table, then the page. `stepwise` is the Mod+A key (Notion, decisions.md "Notion selection verdicts"),
 * in the context the browser runs observed, a top-level text block: the first press selects its text; a press with
 * that text covered whole, or in an empty block, block-selects every body block. Every other selection selects the page
 * range (documentSelection), as do contexts no verdict covers yet.
 */
export function selectAllTarget(blocks: Blocks, selection: CoreSelection | null, stepwise: boolean): CoreSelection {
  const body = blocks.filter(block => blockClass(block) !== 'header')
  if (stepwise && selection?.nodes?.length && selection.nodes.length < body.length &&
    !body.some(block => block.type === 'table' && selection.nodes?.includes(block.id))) {
    return blockRange(body, body[0].id, body.at(-1)?.id ?? body[0].id) ?? documentSelection(blocks)
  }
  const entries = rows(blocks)
  if (entries.length === 0) return documentSelection(blocks)
  const current = selection ?? { anchor: pointAt(entries[0], 0), focus: pointAt(entries[0], 0) }
  const anchor = locate(entries, current.anchor)
  const focus = locate(entries, current.focus)
  const [start, end] = anchor.row > focus.row || (anchor.row === focus.row && anchor.offset > focus.offset)
    ? [focus, anchor] : [anchor, focus]
  const expanded = start.row !== end.row || start.offset !== end.offset
  const cell = entries[anchor.row]
  const table = cell.parents.find((parent) => parent.type === 'table')
  const nodes = current.nodes ?? []
  const observed = stepwise && !table && cell.parents.length === 0 && cell.block.type === 'p'
  const sameRow = anchor.row === focus.row && !current.table && nodes.length === 0
  const local = (table !== undefined || observed) && sameRow &&
    !(start.offset === 0 && end.offset === plain(cell.block).length && (expanded || plain(cell.block).length === 0))
  if (local) return { anchor: pointAt(cell, 0), focus: pointAt(cell, plain(cell.block).length) }
  if (observed && sameRow && body.length > 0) {
    return blockRange(body, body[0].id, body.at(-1)?.id ?? body[0].id) ?? documentSelection(blocks)
  }
  if (table && !nodes.includes(table.id)) {
    const point = { blockId: table.id, offset: 0 }
    return { anchor: point, focus: point, nodes: [table.id] }
  }
  return documentSelection(blocks)
}

function applyCommand(state: CoreState, intent: EditorIntent): void {
  if (intent.type === 'selectAll') {
    state.selection = selectAllTarget(state.blocks, state.selection, intent.stepwise === true)
    return
  }
  const gap = (point: CorePoint) => gapOf(state.blocks, point)
  // A block handle drop moves blocks whatever the selection is (drop.ts); identity-anchored points follow them.
  if (intent.type === 'exitBlock') {
    if (!state.selection) return
    const entries = rows(state.blocks)
    const row = entries[locate(entries, state.selection.focus).row]
    // In a quote Mod+Enter does nothing (Notion browser check notion-browser-queue.md C17, imp07).
    if (!row || row.block.type === 'p' || row.parents.some((parent) => parent.type === 'quote')) return
    const paragraph: Block = { id: '', type: 'p', children: [] }
    insertBlockAfter(state, row.siblings, row.block, paragraph)
    const point = pointAt(rows([paragraph])[0], 0)
    state.selection = { anchor: point, focus: point }
    return
  }
  if (intent.type === 'insertBlockSibling') {
    const index = state.blocks.findIndex(block => block.id === intent.block)
    if (index < 0) return
    const paragraph: Block = { id: '', type: 'p', children: [] }
    spliceBlocks(state, state.blocks, index + (intent.placement === 'after' ? 1 : 0), 0, paragraph)
    const point = pointAt(rows([paragraph])[0], 0)
    state.selection = { anchor: point, focus: point }
    return
  }
  if (intent.type === 'dropBlocks') {
    dropBlocks(state, intent.blocks, intent.target, intent.placement, intent.copy)
    return
  }
  if (intent.type === 'questionAction') {
    questionAction(state, intent)
    return
  }
  if (intent.type === 'listItemAction') {
    listItemAction(state, intent.item, intent.action, (block) => duplicates(state, [block])[0])
    return
  }
  if (intent.type === 'setCodeLanguage') {
    setCodeLanguage(state, intent.block, intent.language)
    return
  }
  if (intent.type === 'resizeColumns') {
    resizeColumns(state, intent.left, intent.right, intent)
    return
  }
  // A text drop composes a paste and a range delete into one change (drop.ts).
  if (intent.type === 'dropText') {
    dropText(state, intent.at, intent.data, (command) => { applyCommand(state, command) })
    return
  }
  // Pasting over a node selection replaces its nodes, as its deletion does (Lexical NodeSelection.insertNodes removes
  // each selected node); its gap endpoints need not cover them. Typing replaces a selection of several blocks (select all
  // from a selected block) the same way; over one selected block it types after it, as at a block cursor.
  const nodes = state.selection?.nodes ?? []
  if (nodes.length === 1 && intent.type === 'insertText') {
    const owner = blockGap(state.blocks, nodes[0])
    const block = owner?.siblings[owner.index]
    const last = block && rows([block]).at(-1)
    const point = last ? pointAt(last, plain(last.block).length) : owner && gapPoint({ ...owner, index: owner.index + 1 })
    if (point) state.selection = { anchor: point, focus: point }
  } else if (nodes.length > 0 && ['insertText', 'insertFromPaste', 'insertParagraph', 'insertLineBreak'].includes(intent.type)) {
    const point = removeSelectedNodes(state, nodes)
    if (point) state.selection = { anchor: point, focus: point }
  }
  // Root element ranges cover boundary decorators as well as the text rows between them.
  // Consume those boundaries before the ordinary row-range replacement path.
  // A node selection keeps its kind: its deletion goes by identity (atomicGesture), never through these gap endpoints.
  if (state.selection && !(state.selection.nodes?.length && intent.type.startsWith('delete')) && (intent.type.startsWith('delete') ||
    ['insertText', 'insertFromPaste', 'insertParagraph', 'insertLineBreak'].includes(intent.type))) {
    const anchorGap = gap(state.selection.anchor)
    const focusGap = gap(state.selection.focus)
    const rootGaps = [anchorGap, focusGap].flatMap(value => value?.siblings === state.blocks ? [value] : [])
    if (rootGaps.length === 2 && rootGaps[0].index !== rootGaps[1].index) {
      const point = removeTopLevel(state, Math.min(rootGaps[0].index, rootGaps[1].index), Math.abs(rootGaps[0].index - rootGaps[1].index))
      state.selection = { anchor: point, focus: point }
      if (intent.type.startsWith('delete')) return
    } else if (rootGaps.length === 1 || (anchorGap === undefined) !== (focusGap === undefined)) {
      // One end is a block gap (at the document level, else in any block list), the other a text position: every block
      // of that list between the gap and the block holding the text position is covered whole (a wrapper or an atom
      // too, not only text), and the gap end becomes that block's near edge.
      const entries = rows(state.blocks)
      const gapIsAnchor = rootGaps.length === 1 ? anchorGap?.siblings === state.blocks : !!anchorGap
      const boundaryGap = gapIsAnchor ? anchorGap : focusGap
      if (entries.length > 0 && boundaryGap) {
        const position = locate(entries, gapIsAnchor ? state.selection.focus : state.selection.anchor)
        const row = entries[position.row]
        const { siblings, index: boundary } = boundaryGap
        const block = siblings.find(item => item === row.block || row.parents.some(parent => parent === item))
        const at = block ? siblings.indexOf(block) : -1
        if (block && at >= 0) {
          const text = pointAt(row, position.offset)
          const [from, count] = boundary <= at ? [boundary, at - boundary] : [at + 1, boundary - at - 1]
          if (siblings === state.blocks) removeTopLevel(state, from, count)
          else spliceBlocks(state, siblings, from, count)
          const own = rows([block])
          const edge = boundary <= at ? pointAt(own[0], 0) : pointAt(lastOf(own), plain(lastOf(own).block).length)
          state.selection = gapIsAnchor ? { anchor: edge, focus: text } : { anchor: text, focus: edge }
        }
      }
    }
  }
  if (rows(state.blocks).length === 0 && !(state.selection && gap(state.selection.anchor) &&
    (intent.type === 'insertFromPaste' || intent.type === 'moveCaret'))) {
    insertBlock(state, state.blocks, state.blocks.length,
      { id: '', type: 'p', children: [] })
  }
  let formatParagraph: Block | undefined
  let entries = rows(state.blocks)
  state.selection ??= { anchor: pointAt(entries[0], 0), focus: pointAt(entries[0], 0) }
  const collapse = (point: CorePoint) => { state.selection = { anchor: point, focus: point } }
  const anchor = locate(entries, state.selection.anchor)
  const focus = locate(entries, state.selection.focus)
  const reversed = anchor.row > focus.row ||
    (anchor.row === focus.row && anchor.offset > focus.offset)
  let start = reversed ? focus : anchor
  let end = reversed ? anchor : focus
  const expanded = start.row !== end.row || start.offset !== end.offset

  /** The marks of a one-row range's first character; a range across rows leaves none (seed/edge/cross-block/bold-to-italic). */
  const rangeFormat = () => start.row === end.row
    ? formatAt(entries[start.row], Math.min(start.offset + 1, plain(entries[start.row].block).length)) : 0
  const deleteRange = () => {
    const first = entries[start.row]
    const last = entries[end.row]
    const acrossSlots = gridSlot(first) !== gridSlot(last)
    if (start.row === end.row) {
      deleteInlineRange(state, first.block.children, start.offset, end.offset)
    } else if (acrossSlots) {
      // Ends in different grid slots (table cells, columns, or one outside): each row loses its selected text and no
      // row merges across a slot's edge (Lexical shadow roots).
      for (let i = start.row; i <= end.row; i++) {
        const row = entries[i]
        deleteInlineRange(state, row.block.children, i === start.row ? start.offset : 0,
          i === end.row ? end.offset : Infinity)
        if (i > start.row && i < end.row && row.block.type !== 'td') remove(state, row)
      }
    } else {
      const tail = takeInlineTail(state, last.block.children, end.offset)
      deleteInlineRange(state, first.block.children, start.offset, Infinity)
      spliceBlocks(state, first.block.children, first.block.children.length, 0, ...tail)
      // One tree-order contract: every block atom the range fully covers goes with its text rows (Lexical removes each
      // selected node); a grid (table, column set) goes only whole, never when the range ends inside it.
      let covered = false
      const prune = (items: Block[]) => {
        for (let i = 0; i < items.length; i += 1) {
          const block = items[i]
          if (block.id === first.block.id) covered = true
          else if (block.id === last.block.id) covered = false
          else if (covered && (block.type === 'table' || block.type === 'cols' ? !rows([block]).some((row) => row.block.id === last.block.id)
            : isSelectableBlock(block) && block.type !== 'pageMeta')) {
            spliceBlocks(state, items, i, 1)
            i -= 1
            continue
          }
          if (!isRow(block)) prune(children(block))
        }
      }
      prune(state.blocks)
      for (let i = end.row; i > start.row; i -= 1) {
        const removed = entries[i]
        const item = removed.parents.at(-1)
        const list = removed.parents.at(-2)
        // A row of another list item merges that item into the first row's; a row of the item holding the first row
        // is only that item's paragraph.
        if (item?.type === 'li' && list && !first.parents.includes(item)) {
          // The item's other blocks (its nested list) stay: a join never drops content (BlockSuite forwardDelete).
          const nested = item.children.filter((child) => child !== removed.block)
          const firstItem = first.parents.at(-1)
          if (firstItem?.type === 'li') {
            spliceBlocks(state, firstItem.children, firstItem.children.length, 0, ...nested)
          } else {
            const promoted = nested.flatMap((child) => child.type === 'list' ? child.children : [])
            spliceBlocks(state, list.children, list.children.indexOf(item) + 1, 0, ...promoted)
          }
          spliceBlocks(state, list.children, list.children.indexOf(item), 1)
        } else remove(state, removed)
      }
    }
    clean(state, state.blocks)
    collapse(pointAt(first, start.offset))
    entries = rows(state.blocks)
    start = {
      row: entries.findIndex((entry) => entry.block.id === first.block.id),
      offset: start.offset,
    }
    end = start
  }

  const anchorGap = gap(state.selection.anchor)
  const focusGap = gap(state.selection.focus)
  if (entries.length === 0 && intent.type === 'moveCaret') {
    const index = focusGap?.index ?? 0
    const offset = intent.unit === 'document' ? intent.direction === 'backward' ? 0 : state.blocks.length
      : Math.max(0, Math.min(state.blocks.length, index + (intent.direction === 'backward' ? -1 : 1)))
    const point = gapPoint({ siblings: state.blocks, index: offset })
    state.selection = { anchor: intent.extend ? state.selection.anchor : point, focus: point }
  } else if (atomicGesture(state, intent)) {
    // Atomic boundaries own deletion and caret transitions.
  } else if (anchorGap && focusGap && (intent.type === 'insertParagraph' || intent.type === 'insertText')) {
    const paragraph: Block = { id: '', type: 'p', children: intent.type === 'insertText' ? [leaf(intent.text)] : [] }
    insertBlock(state, focusGap.siblings, focusGap.index, paragraph)
    collapse(pointAt(rows([paragraph])[0], plain(paragraph).length))
  } else if (anchorGap && focusGap && intent.type === 'insertFromPaste') {
    paste(state, anchorGap, 0, pastedComments(pasteBlocks(intent, false, () => questionIds(pageDocument(state.doc).content)), pageDocument(state.doc).content), intent.html !== undefined || 'fragment' in intent)
  } else if (intent.type === 'applySlashItem') {
    applySlashItem(state, intent.title, intent.block)
  } else if (intent.type === 'insertTable' || intent.type === 'insertDivider' || intent.type === 'insertFormula') {
    // Block content has no place in an inline-only slot (INV-LEVEL): a cell or field refuses it.
    if (inInlineSlot(state, entries[start.row])) return
    if (expanded) deleteRange()
    insertRichBlock(state, entries[start.row], intent.type === 'insertTable'
      ? createTable(intent.rows, intent.columns) : intent.type === 'insertDivider'
      ? { id: '', type: 'hr' }
      : { id: '', type: 'pre', latex: true, children: [{ id: '', type: 'chars', text: [{ id: '', text: intent.source }] }] }, start.offset)
  } else if (intent.type === 'tableControl') {
    editTable(state, entries[start.row], intent.control)
  } else if (intent.type === 'setLink' && intent.links) {
    // An open link editor edits its links by identity (Lexical LinkNode.setURL), wherever peers moved their text.
    for (const id of intent.links) {
      const link = findBlock(state.blocks, id)
      const href = safeLinkHref(intent.url)
      if (link?.type === 'a' && href !== null) setBlockProps(state, link, { href })
    }
  } else if (intent.type === 'setLink' || intent.type === 'removeLink') {
    linkRange(state, entries, start, end, intent.type === 'setLink' ? intent.url : null, reversed)
  } else if (intent.type === 'moveCodeLines' && entries[start.row].block.type === 'pre') {
    moveCodeLines(state, entries[start.row], start.offset, end.offset, intent.direction === 'up')
  } else if (intent.type === 'duplicateBlock' && state.selection.nodes?.length) {
    // Lexical BlockSelectionPlugin Mod+D: every selected block, in document order, copied after the last one; the
    // copies become the node selection.
    const selected = selectedBlocks(state.blocks, state.selection.nodes)
    const last = selected.at(-1)
    const home = last && blockGap(state.blocks, last.id)
    if (!home) return
    const copies = duplicates(state, selected)
    spliceBlocks(state, home.siblings, home.index + 1, 0, ...copies)
    const edge = (block: Block | undefined) => ({ blockId: block?.id ?? '', offset: 0 })
    state.selection = { anchor: edge(copies[0]), focus: edge(copies.at(-1)), nodes: copies.map((copy) => copy.id) }
  } else if (intent.type === 'toggleTodo' || intent.type === 'moveBlock' ||
    intent.type === 'duplicateBlock') {
    const row = entries[anchor.row]
    const owner = row.parents.at(-1)
    const list = row.parents.at(-2)
    const target = owner?.type === 'li' && owner.children[0] === row.block ? owner : row.block
    const siblings = target === owner && list ? list.children : row.siblings
    const index = siblings.indexOf(target)
    if (intent.type === 'toggleTodo') {
      const touched = intent.item === undefined ? entries.slice(start.row, end.row + 1) : entries.filter((entry) => entry.parents.at(-1)?.id === intent.item)
      const items = new Set(touched.flatMap((entry) => {
        const item = entry.parents.at(-1)
        const parent = entry.parents.at(-2)
        return item?.type === 'li' && parent?.type === 'list' && parent.subtype === 'todo'
          ? [item] : []
      }))
      const done = [...items].some((item) => !item.done)
      for (const item of items) setBlockProps(state, item, { done })
    } else if (intent.type === 'duplicateBlock') {
      duplicateCaretBlock(state, row, anchor.offset)
    } else {
      const to = index + (intent.direction === 'up' ? -1 : 1)
      if (to >= 0 && to < siblings.length) {
        spliceBlocks(state, siblings, index, 1)
        spliceBlocks(state, siblings, to, 0, target)
      }
    }
  } else if (intent.type === 'insertFromPaste' && intent.md !== undefined && !intent.literal && expanded &&
    /^https?:\/\/\S+$/u.test(intent.md)) {
    linkRange(state, entries, start, end, intent.md, reversed)
  } else if (intent.type === 'insertFromPaste') {
    const code = entries[start.row].block.type === 'pre'
    if (expanded && !state.selection.table) deleteRange()
    const fragment = pastedComments(pasteBlocks(intent, code, () => questionIds(pageDocument(state.doc).content)), pageDocument(state.doc).content)
    paste(state, entries[start.row], start.offset, fragment, intent.html !== undefined || 'fragment' in intent)
    delete state.pendingFormat
  } else if ((intent.type === 'formatIndent' || intent.type === 'formatOutdent') &&
    entries[start.row].block.type === 'td') {
    const backward = intent.type === 'formatOutdent'
    let next = entries[start.row + (backward ? -1 : 1)]
    if (!next && !backward) {
      editTable(state, entries[start.row], 'add-row')
      entries = rows(state.blocks)
      next = entries[start.row + 1]
    }
    if (next) collapse(pointAt(next, 0))
  } else if (intent.type === 'formatIndent' || intent.type === 'formatOutdent') {
    const targets = entries.slice(start.row, end.row + 1).map((row) => ({
      row,
      depth: row.parents.filter((parent) => parent.type === 'list').length,
    }))
    for (const { row: target, depth } of targets) {
      const current = rows(state.blocks).find((entry) => entry.block.id === target.block.id)
      if (!current) continue
      if (current.block.type === 'pre') {
        const index = entries.indexOf(target)
        indentCode(state, current, index === start.row ? start.offset : 0,
          index === end.row ? end.offset : plain(current.block).length,
          intent.type === 'formatOutdent')
        continue
      }
      const item = current.parents.at(-1)
      if (item?.type !== 'li' || item.children[0] !== current.block) {
        indentItem(state, current, intent.type === 'formatOutdent')
        continue
      }
      const currentDepth = current.parents.filter((parent) => parent.type === 'list').length
      const desired = intent.type === 'formatOutdent' ? Math.max(1, depth - 1)
        : depth + 1
      if (currentDepth !== desired) indentItem(state, current, currentDepth > desired)
    }
  } else if (intent.type === 'formatBlock' && entries.slice(start.row, end.row + 1).some(row => inInlineSlot(state, row))) {
    // A block kind has no place in an inline-only slot (INV-LEVEL): a cell or field row keeps its kind.
  } else if (intent.type === 'formatBlock') {
    if (expanded) formatBlockRange(state, entries, start, end, intent.block)
    else formatBlock(state, entries[anchor.row], intent.block)
    delete state.pendingFormat
  } else if (intent.type === 'moveCaret' && intent.unit === 'document' &&
    intent.direction === 'backward' && state.blocks[0]?.type === 'table') {
    const point = gapPoint({ siblings: state.blocks, index: 0 })
    state.selection = { anchor: intent.extend ? state.selection.anchor : point, focus: point }
  } else if (intent.type === 'moveCaret' && intent.unit === 'document' &&
    intent.direction === 'forward' && state.blocks.at(-1)?.type === 'table') {
    const paragraph: Block = { id: '', type: 'p', children: [] }
    insertBlock(state, state.blocks, state.blocks.length, paragraph)
    const point = { blockId: paragraph.id, offset: 0 }
    state.selection = { anchor: intent.extend ? state.selection.anchor : point, focus: point }
  } else if (intent.type === 'moveCaret') {
    const position = expanded && !intent.extend && intent.unit === 'character'
      ? intent.direction === 'backward' ? start : end
      : move(entries, focus, intent.direction === 'backward' || intent.direction === 'up',
        intent.unit !== 'document' && (intent.direction === 'up' || intent.direction === 'down')
          ? 'vertical' : intent.unit)
    const targetRow = entries[position.row]
    const point = pointAt(targetRow, position.offset)
    state.selection = { anchor: intent.extend ? state.selection.anchor : point, focus: point }
    delete state.pendingFormat
    if (expanded && !intent.extend) {
      const insideLink = inlineLeaves(targetRow.block.children).some(({ block, start, end }) =>
        block.type === 'a' && position.offset > start && position.offset < end)
      state.pendingFormat = formatAt(targetRow, Math.min(plain(targetRow.block).length,
        position.offset + (intent.direction === 'forward' && !insideLink ? 1 : 0)))
    }
  } else if (intent.type in formats && state.selection.table) {
    const bit = formats[intent.type as keyof typeof formats]
    const targets = selectedTableRows(state)
    const leaves = targets.flatMap((entry) => {
      const collect = (block: Block): Block[] => block.type === 'chars' ? [block]
        : children(block).flatMap((child) => collect(child))
      return collect(entry.block)
    })
    const clear = intent.type === 'clearFormatting' || (leaves.length > 0 && leaves.every((block) => block.type === 'chars' && ((block.format ?? 0) & bit) !== 0))
    for (const block of leaves) if (block.type === 'chars') setBlockProps(state, block,
      { format: clear ? (block.format ?? 0) & ~bit : (block.format ?? 0) | bit })
  } else if (intent.type in formats) {
    const bit = formats[intent.type as keyof typeof formats]
    if (expanded) {
      const selected = entries.slice(start.row, end.row + 1).flatMap((entry, index) =>
        sliceInline(entry.block.children, index === 0 ? start.offset : 0,
          start.row + index === end.row ? end.offset : Infinity, false, state),
      )
      const marked: number[] = []
      const collect = (block: Block) => {
        if (block.type === 'chars') marked.push(block.format ?? 0)
        children(block).forEach((block) => { collect(block) })
      }
      selected.forEach((block) => { collect(block) })
      // Nothing formatted to clear: Lexical's clear formatting leaves the text nodes as they are.
      if (intent.type === 'clearFormatting' && marked.every((format) => (format & bit) === 0)) return
      // :uc[cbe-ux-format-empty-range]: a range holding no characters formats nothing and writes nothing to the Doc.
      if (marked.length === 0) return
      // :uc[cbe-ux-mark-chord-whitespace-only]: Notion leaves a whitespace-only selection unformatted (a mark chord
      // over only spaces is a no-op; clearFormatting still clears). A character typed over the range stays plain.
      if (intent.type !== 'clearFormatting' && selected.map((block) => plain(block)).join('').trim() === '') return
      const clear = intent.type === 'clearFormatting' || (marked.length > 0 && marked.every((format) => (format & bit) !== 0))
      const format = (block: Block) => {
        if (block.type === 'chars') block.format = clear
          ? (block.format ?? 0) & ~bit : (block.format ?? 0) | bit
        children(block).forEach((block) => { format(block) })
      }
      // An inline element the range cuts (a link) stays one element with its id; only its leaves split and take the
      // format. Copying the element would leave the command two same-href links to rejoin, and rejoining deletes a link
      // a peer may be inserting into (:uc[cbe-core-leaves-keep-identity]; Lexical formats text nodes, not links).
      const formatRange = (items: Blocks, from: number, to: number): Blocks => {
        let offset = 0
        return items.flatMap((block) => {
          const length = plain(block).length
          const lo = Math.max(0, from - offset)
          const hi = Math.min(length, to - offset)
          offset += length
          if (lo >= hi) return [block]
          if (children(block).length > 0) return [{ ...block, children: formatRange(children(block), lo, hi) } as Block]
          const middle = sliceInline([block], lo, hi, lo > 0, state)
          middle.forEach((each) => { format(each) })
          return [...sliceInline([block], 0, lo, false, state), ...middle, ...sliceInline([block], hi, Infinity, true, state)]
        })
      }
      for (let i = start.row; i <= end.row; i += 1) {
        const entry = entries[i]
        const from = i === start.row ? start.offset : 0
        const to = i === end.row ? end.offset : plain(entry.block).length
        replaceChildren(state, entry.block, formatRange(entry.block.children, from, to))
      }
      clean(state, state.blocks)
      const left = pointAt(entries[start.row], start.offset)
      const right = pointAt(entries[end.row], end.offset)
      state.selection = { anchor: reversed ? right : left, focus: reversed ? left : right }
    } else if (intent.type !== 'clearFormatting') {
      const current = state.pendingFormat ?? formatAt(entries[start.row], start.offset)
      state.pendingFormat = current ^ bit
    }
  } else if (intent.type.startsWith('delete') && state.selection.table) {
    const selected = selectedTableRows(state)
    const first = selected[0]
    const table = first?.parents.find((parent) => parent.type === 'table')
    if (table && selected.length === rows([table]).length) {
      const parent = first.parents[first.parents.indexOf(table) - 1]
      const siblings = parent ? children(parent) : state.blocks
      const index = siblings.indexOf(table)
      spliceBlocks(state, siblings, index, 1)
      collapse(gapPoint({ siblings, parent, index }))
    } else {
      for (const entry of selected) replaceChildren(state, entry.block, [])
      if (first) collapse(pointAt(first, 0))
    }
  } else if (intent.type.startsWith('delete')) {
    // After a deleted range the next typed text takes the format before the range, not the deleted text's (Notion browser
    // check imp21: italic "bc" selected, Backspace, then x: x is plain; ProseMirror deleteSelection keeps the range's).
    let pendingFormat = expanded ? formatAt(entries[start.row], start.offset)
      : state.pendingFormat ?? formatAt(entries[start.row], start.offset)
    if (expanded && start.row === 0 && start.offset === 0 &&
      state.blocks[0]?.id === (entries[0].parents[0] ?? entries[0].block).id &&
      state.blocks.at(-1)?.id === (lastOf(entries).parents[0] ?? lastOf(entries).block).id &&
      end.row === entries.length - 1 && end.offset === plain(lastOf(entries).block).length &&
      !entries[0].parents.some((parent) => parent.type === 'list' &&
        lastOf(entries).parents.includes(parent))) {
      const paragraph: Block = { id: entries[0].block.id, type: 'p', children: [] }
      spliceBlocks(state, state.blocks, 0, state.blocks.length, paragraph)
      collapse({ blockId: paragraph.id, offset: 0 })
      // Deleting the whole document leaves no marks (cbe-ux-select-all-delete).
      pendingFormat = 0
    } else if (expanded) deleteRange()
    else {
      const backward = intent.type.endsWith('Backward')
      const unit = intent.type.includes('Word') ? 'word'
        : intent.type.includes('SoftLine') ? 'line' : 'character'
      let target = move(entries, start, backward, unit)
      if (unit === 'line' && target.row === start.row && target.offset === start.offset) {
        target = move(entries, start, backward, 'character')
      }
      const slot = gridSlot(entries[start.row])
      const text = plain(entries[start.row].block)
      if (backward && unit === 'character' && entries[start.row].block.type === 'pre' && start.offset > 0 &&
        CODE_PAIRS[text[start.offset - 1]] !== undefined && CODE_PAIRS[text[start.offset - 1]] === text[start.offset]) {
        // Backspace inside an empty code pair removes both delimiters (BlockSuite code-delete-empty-pair).
        end = { row: start.row, offset: start.offset + 1 }
        start = target
        deleteRange()
      } else if (target.row !== start.row && slot && gridSlot(entries[target.row]) !== slot) {
        collapse(pointAt(entries[start.row], start.offset))
      } else if (joinBoundary(state, entries, start, backward)) {
        // Structural boundary command has already mapped the caret.
      } else {
        end = backward ? start : target
        start = backward ? target : start
        const gap = entries[end.row]
        const at = gap.siblings.indexOf(gap.block)
        const before = gap.siblings[at - 1]
        const after = gap.siblings[at + 1]
        deleteRange()
        if (before?.type === 'list' && after?.type === 'list' && before.subtype === after.subtype) {
          const index = gap.siblings.indexOf(before)
          if (index >= 0 && gap.siblings[index + 1] === after) joinBlocks(state, before, after)
        }
      }
    }
    state.pendingFormat = pendingFormat
  } else if (intent.type === 'insertText' || intent.type === 'insertLineBreak' ||
    intent.type === 'insertParagraph') {
    if (intent.type === 'insertText' && expanded && start.row === end.row) {
      let offset = 0
      const replaceAnnotated = (block: Block, annotated: boolean): boolean => {
        const nested = children(block)
        if (nested.length > 0) return nested.some(child => replaceAnnotated(child,
          annotated || ['em', 'l', 'cloze', 'comment'].includes(block.type)))
        const from = offset
        offset += plain(block).length
        if (!annotated || block.type !== 'chars' || start.offset < from || end.offset > offset) return false
        editText(state, block, start.offset - from, end.offset - start.offset, intent.text)
        collapse({ blockId: block.id, offset: start.offset - from + intent.text.length })
        return true
      }
      if (replaceAnnotated(entries[start.row].block, false)) return
    }
    if (expanded && intent.type === 'insertText' && start.row === end.row) state.pendingFormat = rangeFormat()
    if (expanded) deleteRange()
    const row = entries[start.row]
    const offset = start.offset
    // A code block pairs delimiters: an opener types its closer after the caret, a closer before the same closer steps
    // over it (BlockSuite code autopair; Backspace inside an empty pair is in the delete branch).
    const pairing = intent.type === 'insertText' && row.block.type === 'pre' && !expanded
    const stepsOver = pairing && Object.values(CODE_PAIRS).includes(intent.text) && plain(row.block)[offset] === intent.text
    const closer = pairing && !stepsOver ? CODE_PAIRS[intent.text] : undefined
    if (stepsOver) {
      collapse(pointAt(row, offset + 1))
    } else if (intent.type === 'insertText') {
      const format = insertionFormat(state, row, offset)
      const content = row.block.children
      if (!insertChars(state, content, offset, intent.text, format)) {
        const tail = takeInlineTail(state, content, offset)
        spliceBlocks(state, content, content.length, 0,
          ...(intent.text ? [leaf(intent.text, format)] : []), ...tail)
      }
      if (closer) insertChars(state, content, offset + intent.text.length, closer, format)
      clean(state, state.blocks)
      collapse(pointAt(row, offset + intent.text.length))
      // Text insertion keeps this row live; structural commands retain full reconciliation.
      if (!expanded) formatParagraph = row.block
    } else if (intent.type === 'insertParagraph' && row.block.type === 'td' &&
      (!plain(row.block) || cellBelow(row) !== undefined)) {
      // Enter moves to the cell below in the same grid column (spans counted, table-grid); a filled cell does so only
      // when a row below exists (Notion browser check run3.out:41), else it adds a line. An empty last-row cell leaves
      // for the paragraph after the table in the table's own container (BlockNote TableExtension.ts:37).
      const cell = cellBelow(row)
      const table = row.parents.findLast((parent) => parent.type === 'table')
      if (cell) collapse(pointAt(rows([cell])[0], 0))
      else if (table) {
        const owner = row.parents[row.parents.indexOf(table) - 1]
        const siblings = owner ? children(owner) : state.blocks
        const index = siblings.indexOf(table)
        let paragraph = siblings[index + 1]
        if (paragraph?.type !== 'p') {
          paragraph = { id: '', type: 'p', children: [] }
          insertBlock(state, siblings, index + 1, paragraph)
        }
        collapse(pointAt(rows([paragraph])[0], 0))
      }
    } else if (intent.type === 'insertLineBreak' ||
      intent.type === 'insertParagraph' && inInlineSlot(state, row)) {
      // The marks at the caret carry over the break (BlockNote Shift+Enter keeps the active marks).
      state.pendingFormat ??= formatAt(row, offset)
      const tail = takeInlineTail(state, row.block.children, offset)
      spliceBlocks(state, row.block.children, row.block.children.length, 0,
        { id: '', type: 'br' }, ...tail)
      collapse(pointAt(row, offset + 1))
    } else if (row.block.type === 'p' && plain(row.block) === '```' && offset === 3 &&
      !row.parents.some((parent) => parent.type === 'li')) {
      const code: Block = { id: row.block.id, type: 'pre', children: [] }
      replaceBlock(state, row.block, code)
      collapse({ blockId: code.id, offset: 0 })
    } else {
      if (plain(row.block)) state.pendingFormat ??= formatAt(row, offset)
      split(state, row, offset, intent.type === 'insertParagraph' && intent.collapsed === true)
    }
  }
  if (state.blocks.length === 0) {
    const paragraph: Block = { id: '', type: 'p', children: [] }
    insertBlock(state, state.blocks, 0, paragraph)
    collapse({ blockId: paragraph.id, offset: 0 })
  }
  const normalized = canonicalDraft(state.blocks)
  // Normalization may replace row objects. Only reuse the resolved row when identity survived.
  if (normalized !== state.blocks) formatParagraph = undefined
  state.blocks = normalized
  state.pendingFormat ??= emptyParagraphFormat(state, formatParagraph)
  rememberEmptyParagraphFormat(state, formatParagraph)
}

/** The cell under `row`'s cell in the same grid column, past its rowSpan (tableGrid), if the table has one. */
function cellBelow(row: Row): Block | undefined {
  const table = row.parents.findLast((parent) => parent.type === 'table')
  if (table?.type !== 'table') return undefined
  const grid = tableGrid(table)
  const y = grid.findIndex((cells) => cells.some((cell) => cell.id === row.block.id))
  const x = y < 0 ? -1 : grid[y].findIndex((cell) => cell.id === row.block.id)
  const span = row.block.type === 'td' ? row.block.rowSpan ?? 1 : 1
  return x < 0 ? undefined : grid[y + span]?.[x]
}

